// MailSettingsConfiguration.js

import React, { useState, useEffect } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

// Standard port per SMTP Secure mode — used only to pre-fill the Port field
// when Secure changes, matching www/api/shared/mail_helper.php's
// default_smtp_port(). The field stays editable for a non-standard provider.
const DEFAULT_SMTP_PORT = { ssl: 465, tls: 587, '': 25 };

function MailSettingsConfiguration() {
  const navigate = useNavigate();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [settings, setSettings] = useState({
    sendEmail: false,
    mailMethod: 'phpmail',
    smtpHost: '',
    smtpAuth: true,
    smtpUsername: '',
    smtpPassword: '',
    smtpSecure: 'ssl',
    smtpPort: DEFAULT_SMTP_PORT.ssl,
    fromEmail: '',
    fromName: ''
  });
  const [loading, setLoading] = useState(true);
  const [smtpPasswordSet, setSmtpPasswordSet] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE_URL}/mail/get_mail_settings.php`, { credentials: 'include' })
      .then(response => response.json())
      .then(data => {
        if (data && data.id) {
          setSettings({
            sendEmail: !!Number(data.send_email),
            mailMethod: data.mail_method || 'phpmail',
            smtpHost: data.smtp_host || '',
            smtpAuth: data.smtp_auth === null ? true : !!Number(data.smtp_auth),
            smtpUsername: data.smtp_username || '',
            smtpPassword: data.smtp_password || '',
            smtpSecure: data.smtp_secure || 'ssl',
            smtpPort: data.smtp_port || DEFAULT_SMTP_PORT[data.smtp_secure || 'ssl'],
            fromEmail: data.from_email || '',
            fromName: data.from_name || ''
          });
          setSmtpPasswordSet(!!data.smtp_password_set);
        }
      })
      .catch(error => console.error('Error fetching mail settings:', error))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = e => {
    const { name, value } = e.target;
    setSettings(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = e => {
    const { name, checked } = e.target;
    setSettings(prev => ({ ...prev, [name]: checked }));
  };

  const handleSecureChange = e => {
    const smtpSecure = e.target.value;
    setSettings(prev => ({ ...prev, smtpSecure, smtpPort: DEFAULT_SMTP_PORT[smtpSecure] }));
  };

  const handleSave = () => {
    const formData = new FormData();
    formData.append('sendEmail', settings.sendEmail ? 1 : 0);
    formData.append('mailMethod', settings.mailMethod);
    formData.append('smtpHost', settings.smtpHost);
    formData.append('smtpAuth', settings.smtpAuth ? 1 : 0);
    formData.append('smtpUsername', settings.smtpUsername);
    formData.append('smtpPassword', settings.smtpPassword);
    formData.append('smtpSecure', settings.smtpSecure);
    formData.append('smtpPort', settings.smtpPort);
    formData.append('fromEmail', settings.fromEmail);
    formData.append('fromName', settings.fromName);

    fetch(`${API_BASE_URL}/mail/save_mail_settings.php`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          if (settings.smtpPassword) {
            setSmtpPasswordSet(true);
            setSettings(prev => ({ ...prev, smtpPassword: '' }));
          }
          Swal.fire({ icon: 'success', text: 'Mail settings saved.' });
        } else {
          Swal.fire({ icon: 'error', text: 'Failed to save mail settings: ' + data.message });
        }
      })
      .catch(error => {
        console.error('Error:', error);
        Swal.fire({ icon: 'error', text: 'An error occurred: ' + error.message });
      });
  };

  const handleCancel = () => {
    navigate('/configuration');
  };

  if (loading) {
    return <p>Loading...</p>;
  }

  return (
    <div className='m-2'>
      <h2>Mail Settings</h2>
      <Form>
        <Form.Group controlId="sendEmail">
          <Form.Check
            type="checkbox"
            name="sendEmail"
            label="Send emails to users"
            checked={settings.sendEmail}
            onChange={handleCheckboxChange}
          />
        </Form.Group>

        <Form.Group controlId="mailMethod" className="mt-3">
          <Form.Label>Mail Method</Form.Label>
          <div>
            <Form.Check
              inline
              type="radio"
              name="mailMethod"
              id="mailMethod-phpmail"
              label="PHP mail()"
              value="phpmail"
              checked={settings.mailMethod === 'phpmail'}
              onChange={handleChange}
            />
            <Form.Check
              inline
              type="radio"
              name="mailMethod"
              id="mailMethod-smtp"
              label="SMTP"
              value="smtp"
              checked={settings.mailMethod === 'smtp'}
              onChange={handleChange}
            />
          </div>
        </Form.Group>

        {settings.mailMethod === 'smtp' && (
          <div className="mt-2 ps-3 border-start">
            <Form.Group controlId="smtpHost" className="mt-2">
              <Form.Label>SMTP Host</Form.Label>
              <Form.Control type="text" name="smtpHost" value={settings.smtpHost} onChange={handleChange} />
            </Form.Group>
            <Form.Group controlId="smtpAuth" className="mt-2">
              <Form.Check
                type="checkbox"
                name="smtpAuth"
                label="SMTP Auth"
                checked={settings.smtpAuth}
                onChange={handleCheckboxChange}
              />
            </Form.Group>
            {settings.smtpAuth && (
              <>
                <Form.Group controlId="smtpUsername" className="mt-2">
                  <Form.Label>SMTP Username</Form.Label>
                  <Form.Control type="text" name="smtpUsername" value={settings.smtpUsername} onChange={handleChange} />
                </Form.Group>
                <Form.Group controlId="smtpPassword" className="mt-2">
                  <Form.Label>SMTP Password</Form.Label>
                  <Form.Control
                    type="password"
                    name="smtpPassword"
                    value={settings.smtpPassword}
                    onChange={handleChange}
                    autoComplete="new-password"
                    placeholder={smtpPasswordSet ? '•••••••• (leave blank to keep current)' : ''}
                  />
                  {smtpPasswordSet && (
                    <Form.Text className="text-muted">
                      A password is already saved. Leave this blank to keep it, or type a new one to replace it.
                    </Form.Text>
                  )}
                </Form.Group>
              </>
            )}
            <Form.Group controlId="smtpSecure" className="mt-2">
              <Form.Label>SMTP Secure</Form.Label>
              <Form.Control as="select" name="smtpSecure" value={settings.smtpSecure} onChange={handleSecureChange}>
                <option value="ssl">SSL</option>
                <option value="tls">TLS</option>
                <option value="">None</option>
              </Form.Control>
            </Form.Group>
            <Form.Group controlId="smtpPort" className="mt-2">
              <Form.Label>SMTP Port</Form.Label>
              <Form.Control type="number" name="smtpPort" value={settings.smtpPort} onChange={handleChange} />
            </Form.Group>
          </div>
        )}

        <Form.Group controlId="fromEmail" className="mt-3">
          <Form.Label>From Email</Form.Label>
          <Form.Control type="email" name="fromEmail" value={settings.fromEmail} onChange={handleChange} />
        </Form.Group>
        <Form.Group controlId="fromName" className="mt-2">
          <Form.Label>From Name</Form.Label>
          <Form.Control type="text" name="fromName" value={settings.fromName} onChange={handleChange} />
        </Form.Group>
      </Form>
      <div className='mt-3'>
        <Button variant="primary" className="mr-2" onClick={handleSave}>Save</Button>
        <Button variant="secondary" onClick={handleCancel}>Cancel</Button>
      </div>
    </div>
  );
}

export default MailSettingsConfiguration;
